import React from 'react';
import { Link } from 'react-router-dom';
import { FiMail, FiTrash2, FiAlertTriangle, FiClock, FiUser, FiCheckCircle, FiLogIn } from 'react-icons/fi';
import { useAuth } from '../hooks/useAuth.jsx';

const DELETE_EMAIL = 'khidma046@gmail.com';

const userTypeLabel = (type) => {
  if (type === 'provider') return 'مقدم خدمة';
  if (type === 'client') return 'عميل';
  return '';
};

const DeleteAccountScreen = () => {
  const { user, userType, loading, isProvider, isClient } = useAuth();
  const registeredEmail = user?.email || '';
  const accountType = userTypeLabel(userType);

  const launchDeleteEmail = () => {
    const subject = encodeURIComponent('طلب حذف حساب - تطبيق خدمة');
    const body = encodeURIComponent(
      `مرحباً،\n\nأرغب في حذف حسابي من تطبيق خدمة.\n\nالبريد الإلكتروني المسجّل: ${registeredEmail || ''}\nنوع الحساب (عميل / مقدم خدمة): ${accountType || ''}\n\nشكراً.`
    );
    window.open(`mailto:${DELETE_EMAIL}?subject=${subject}&body=${body}`, '_blank');
  };

  return (
    <div dir="rtl" className="min-h-screen bg-[#F6F6F4]" style={{ fontFamily: "'Tajawal', sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cairo:wght@700;800&family=Tajawal:wght@400;500;700&display=swap');
        .font-display { font-family: 'Cairo', sans-serif; }
      `}</style>

      <div className="bg-khidma-black text-white">
        <div className="max-w-5xl mx-auto px-4 md:px-8 py-10 md:py-14 text-center">
          <div className="inline-flex bg-white/10 rounded-full p-3.5 mb-4">
            <FiTrash2 className="text-white text-2xl" />
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-extrabold mb-2">حذف الحساب</h1>
          <p className="text-white/70 max-w-lg mx-auto text-sm md:text-base leading-relaxed">
            يمكنك طلب حذف حسابك وبياناتك المرتبطة به في أي وقت. لا يلزم تسجيل الدخول للوصول إلى هذه الصفحة.
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 md:px-8 -mt-6 md:-mt-8 pb-12 md:pb-16">

        {!loading && user && (
          <div className="bg-green-50 border border-green-200 rounded-2xl p-5 md:p-6 mb-6 shadow-sm">
            <div className="flex items-start gap-4">
              <div className="bg-green-100 rounded-full p-3 shrink-0">
                <FiUser className="text-green-700 text-xl" />
              </div>
              <div className="flex-1">
                <p className="font-display font-bold text-gray-900 mb-1">أنت مسجّل الدخول حالياً</p>
                <p className="text-sm text-gray-600">
                  البريد المرتبط بحسابك: <span className="font-medium text-gray-900">{registeredEmail}</span>
                  {accountType && <> — نوع الحساب: <span className="font-medium text-gray-900">{accountType}</span></>}
                </p>
                {isProvider && (
                  <Link
                    to="/provider/profile"
                    className="inline-block mt-3 bg-red-600 text-white rounded-lg px-4 py-2 text-sm font-bold hover:bg-red-700 transition-colors"
                  >
                    حذف حسابي الآن من الملف الشخصي
                  </Link>
                )}
                {isClient && (
                  <p className="text-xs text-gray-500 mt-2">
                    كعميل، أرسل طلب الحذف عبر البريد أدناه — سيُربط الطلب تلقائياً ببريدك المسجّل.
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {!loading && !user && (
          <div className="bg-white border border-gray-200 rounded-2xl p-5 md:p-6 mb-6 shadow-sm">
            <div className="flex items-start gap-4">
              <div className="bg-gray-100 rounded-full p-3 shrink-0">
                <FiLogIn className="text-gray-700 text-xl" />
              </div>
              <div>
                <p className="font-display font-bold text-gray-900 mb-1">لست مسجّل الدخول</p>
                <p className="text-sm text-gray-600 leading-relaxed">
                  يمكنك إرسال طلب الحذف عبر البريد مع ذكر البريد الإلكتروني المسجّل في التطبيق.
                  أو{' '}
                  <Link to="/auth" className="text-blue-600 font-medium hover:underline">
                    سجّل الدخول
                  </Link>{' '}
                  لربط الطلب تلقائياً بحسابك.
                </p>
              </div>
            </div>
          </div>
        )}

        <button
          onClick={launchDeleteEmail}
          className="w-full bg-white border border-gray-200 rounded-2xl p-6 md:p-8 hover:shadow-lg transition-all duration-200 flex flex-col sm:flex-row items-center gap-4 sm:gap-6 shadow-sm group mb-8 text-center sm:text-right"
        >
          <div className="bg-red-100 rounded-full p-4 group-hover:scale-110 transition-transform shrink-0">
            <FiMail className="text-red-600 text-2xl" />
          </div>
          <div className="flex-1">
            <span className="font-display font-bold text-gray-900 text-lg block">طلب حذف الحساب عبر البريد</span>
            <span className="text-sm text-gray-500 mt-1 block">{DELETE_EMAIL}</span>
            <span className="text-xs text-gray-400 mt-2 block">
              {registeredEmail
                ? 'سيُعبّأ بريدك المسجّل تلقائياً في رسالة الطلب'
                : 'اضغط لفتح تطبيق البريد وإرسال طلب الحذف'}
            </span>
          </div>
        </button>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
            <div className="flex items-start gap-4">
              <div className="bg-gray-100 rounded-full p-3 shrink-0">
                <FiUser className="text-khidma-black text-xl" />
              </div>
              <div>
                <h2 className="font-display font-bold text-gray-900 mb-2">مقدمو الخدمة (مسجّل الدخول)</h2>
                <p className="text-sm text-gray-600 leading-relaxed">
                  من صفحة الملف الشخصي → زر «حذف الحساب نهائياً»، أو{' '}
                  <Link to="/provider/profile" className="text-blue-600 hover:underline">اضغط هنا</Link>.
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
            <div className="flex items-start gap-4">
              <div className="bg-gray-100 rounded-full p-3 shrink-0">
                <FiMail className="text-khidma-black text-xl" />
              </div>
              <div>
                <h2 className="font-display font-bold text-gray-900 mb-2">العملاء أو من لا يستطيع الدخول</h2>
                <p className="text-sm text-gray-600 leading-relaxed">
                  أرسل طلباً إلى {DELETE_EMAIL} مع ذكر البريد المسجّل في التطبيق لربط الطلب بصاحب الحساب.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl p-5 md:p-6 mb-8 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <FiCheckCircle className="text-green-600 text-xl" />
            <h2 className="font-display text-lg font-bold text-gray-900">كيف نربط الطلب بصاحب الحساب؟</h2>
          </div>
          <ul className="space-y-2 text-sm text-gray-600 leading-relaxed list-disc list-inside">
            <li>إذا كنت مسجّل الدخول، نعرض بريدك المسجّل ونضمّنه تلقائياً في رسالة الطلب.</li>
            <li>إذا لم تكن مسجّل الدخول، اذكر في البريد نفس العنوان الذي استخدمته عند التسجيل.</li>
            <li>نتحقق من ملكية الحساب عبر البريد المسجّل قبل تنفيذ الحذف.</li>
            <li>قد نطلب تأكيداً إضافياً إذا كان الطلب من بريد مختلف عن المسجّل.</li>
          </ul>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl p-5 md:p-6 mb-8 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <FiCheckCircle className="text-green-600 text-xl" />
            <h2 className="font-display text-lg font-bold text-gray-900">ماذا يحدث عند حذف الحساب؟</h2>
          </div>
          <ul className="space-y-2 text-sm text-gray-600 leading-relaxed list-disc list-inside">
            <li>يتم حذف بيانات حسابك الشخصية من المنصة بشكل دائم.</li>
            <li>تُزال معلومات ملفك الشخصي وبطاقة العضوية (إن وُجدت).</li>
            <li>قد تُحذف أو تُخفى المحادثات والسجلات المرتبطة بحسابك وفق سياسة الاحتفاظ بالبيانات.</li>
            <li>يجب تسوية أي مستحقات مالية معلّقة في المحفظة قبل إتمام الحذف.</li>
          </ul>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 md:p-6 flex items-start gap-4 mb-8">
          <div className="bg-amber-100 rounded-full p-3 shrink-0">
            <FiAlertTriangle className="text-amber-600 text-xl" />
          </div>
          <div>
            <p className="font-display font-bold text-gray-900 text-sm md:text-base">تنبيه</p>
            <p className="text-xs md:text-sm text-gray-600 mt-1 leading-relaxed">
              حذف الحساب عملية نهائية ولا يمكن التراجع عنها. تأكد من سحب أي أرصدة متبقية في محفظتك قبل تقديم الطلب.
            </p>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl p-5 md:p-6 flex items-center gap-4 shadow-sm">
          <div className="bg-blue-100 rounded-full p-3 shrink-0">
            <FiClock className="text-blue-600 text-xl" />
          </div>
          <div>
            <p className="font-display font-bold text-gray-900 text-sm md:text-base">مدة معالجة الطلب</p>
            <p className="text-xs md:text-sm text-gray-500 mt-0.5">
              نراجع طلبات حذف الحساب خلال 7 أيام عمل كحد أقصى من تاريخ استلام الطلب عبر البريد الإلكتروني.
            </p>
          </div>
        </div>

        <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
          <a
            href="/contact-us"
            className="text-center bg-khidma-black text-white rounded-lg px-6 py-2.5 text-sm font-bold hover:bg-gray-800 transition-colors"
          >
            مركز المساعدة
          </a>
          <a
            href="/rules-and-terms"
            className="text-center border border-gray-300 text-gray-700 rounded-lg px-6 py-2.5 text-sm font-bold hover:bg-gray-50 transition-colors"
          >
            القواعد والشروط
          </a>
          <a
            href="/privacy-policy"
            className="text-center border border-emerald-300 text-emerald-700 bg-emerald-50 rounded-lg px-6 py-2.5 text-sm font-bold hover:bg-emerald-100 transition-colors"
          >
            سياسة الخصوصية
          </a>
        </div>
      </div>
    </div>
  );
};

export default DeleteAccountScreen;
